import React, { useEffect } from "react";

import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import EmptyCardDataOperationsData from "assets/images/EmptyCardDataOperationsData.png";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { TableSkeletonLoader } from "components/Table-skeleton-loader/TableSkeletonLoader";
import { useActionHook, useFormatDateHook } from "hooks";
import { useDealActions } from "store";

import { FeeItemManage } from "./FeeItemManage";

interface Props {
  feesFormState: { id?: number } | null;
  setFeesFormState: (data: { id?: number } | null) => void;
  dealId: number;
  setIsEmpty: (value: boolean) => void;
}

export const ManagementFees: React.FC<Props> = ({ feesFormState, setFeesFormState, dealId, setIsEmpty }) => {
  const { getMeasureFessTableAction } = useDealActions();
  const formatDate = useFormatDateHook();

  const { action: getTable, data, loading } = useActionHook(getMeasureFessTableAction);

  useEffect(() => {
    getTable(dealId);
  }, []);

  useEffect(() => {
    if (!loading) {
      setIsEmpty(!data?.rows.length);
    }
  }, [loading, data?.rows.length]);

  return (
    <>
      {!loading && !data?.rows.length && (
        <EmptyCard
          title={EMPTY_CARD_CONTENT.managementFees.title}
          description={{
            text: EMPTY_CARD_CONTENT.managementFees.description,
            externalText: EMPTY_CARD_CONTENT.managementFees.externalDescription
          }}
          image={{
            src: EmptyCardDataOperationsData,
            sx: { width: 315, height: 140 }
          }}
          actionsElement={
            <Button variant="contained" onClick={() => setFeesFormState({})} startIcon={<AddIcon />} sx={{ bgcolor: "#071F35" }}>
              Add Fee
            </Button>
          }
        />
      )}
      {loading && (
        <TableContainer component={Paper} sx={{ boxShadow: "sm", borderRadius: "8px", border: "1px solid #dbe8f5" }}>
          <TableSkeletonLoader rowsNum={10} />
        </TableContainer>
      )}

      {!loading && Boolean(data?.rows.length) && (
        <TableContainer component={Paper} sx={{ boxShadow: "sm", borderRadius: "8px", border: "1px solid #dbe8f5" }}>
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: 650
            }}
            aria-label="simple table"
          >
            <TableHead>
              <TableRow>
                {data?.columns.map((col) => (
                  <TableCell
                    sx={{
                      color: "#071F35",
                      fontSize: "14px",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden"
                    }}
                    key={col.key}
                    align="left"
                  >
                    {col.title}
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.rows.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    ":hover": { ".icon": { opacity: 1 } },
                    ".icon": { opacity: 0, transition: "opacity 0.3s linear" },
                    "&:last-child td, &:last-child th": { border: 0 }
                  }}
                >
                  {data.columns.map((col) => {
                    const cell = row.cells[col.key];
                    const value: string =
                      col.key === "date"
                        ? formatDate({ date: cell.value })
                        : `${cell.prefix || ""}${cell.value}${cell.postfix || ""}`;

                    return (
                      <TableCell
                        sx={{
                          color: "#071F35",
                          fontSize: "14px",
                          fontWeight: 400,
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          borderBottom: "1px solid #DBE8F5"
                        }}
                        key={cell.key + row.id}
                        align="left"
                      >
                        {value}
                      </TableCell>
                    );
                  })}
                  <TableCell
                    sx={{
                      borderBottom: "1px solid #DBE8F5"
                    }}
                  >
                    <IconButton className="icon" onClick={() => setFeesFormState({ id: row.id })}>
                      <EditOutlinedIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <FeeItemManage
        formState={feesFormState}
        onClose={() => setFeesFormState(null)}
        dealId={dealId}
        refetchTable={() => getTable(dealId)}
      />
    </>
  );
};
